/**
 * InvoiceSheetShell — pure visual chrome for the Telegram-native payment sheet.
 * No payment logic, no coupon. Used by PaymentSheet (payments) and the SkuDetail
 * preview (navigation). Default test-ids match the legacy InvoiceBottomSheet so
 * existing E2E selectors keep working.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from '../chrome/Button';
import styles from './InvoiceSheet.module.css';

/**
 * Invoice thumbnail: renders the R2 example image, but falls back to the
 * per-SKU `glyph` (or ✦) if the image is missing/404s — so a not-yet-uploaded
 * asset degrades to a distinct icon instead of a broken-image box.
 */
function InvoiceThumb({
  thumbUrl,
  glyph,
  className,
}: {
  thumbUrl?: string;
  glyph?: ReactNode;
  className?: string;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  if (thumbUrl && !failed) {
    return <img src={thumbUrl} alt="" className={className} onError={() => setFailed(true)} />;
  }
  return <>{glyph ?? '✦'}</>;
}

export interface InvoiceSheetShellProps {
  /** SKU code — only for i18n keys / test ids, not for pricing. */
  skuCode: string;
  title: string;
  subtitle?: string;
  priceStars: number;
  bullets: string[];
  /** Trusted i18n HTML override for the balance line. */
  balanceHtml?: string;
  thumbUrl?: string;
  glyph?: ReactNode;
  /** Decorative background behind the sheet (default: dimmed blur backdrop). */
  background?: ReactNode;
  /** Primary CTA label (e.g. "Pay 99 ⭐" or "Proceed"). */
  ctaLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  onCancel: () => void;
  /** Slot rendered between the balance note and the primary button (coupon block). */
  children?: ReactNode;
  testId?: string;
  payTestId?: string;
  cancelTestId?: string;
}

export function InvoiceSheetShell({
  skuCode,
  title,
  subtitle,
  priceStars,
  bullets,
  balanceHtml,
  thumbUrl,
  glyph,
  background,
  ctaLabel,
  onPrimary,
  primaryDisabled = false,
  onCancel,
  children,
  testId = 'invoice-bottom-sheet',
  payTestId = 'invoice-pay-btn',
  cancelTestId = 'invoice-cancel-btn',
}: InvoiceSheetShellProps): JSX.Element {
  const { t } = useTranslation('flows');
  const providerLabel = t('avatarFlow.invoice.provider', { defaultValue: 'Telegram · Payment' });
  const cancelLabel = t('avatarFlow.invoice.cancel', { defaultValue: 'Cancel' });
  const balanceFallback = t('avatarFlow.invoice.balanceNote', {
    defaultValue: 'Stars deducted from your Telegram balance · no card forms.',
  });

  return (
    <div className={styles.root} role="dialog" aria-modal="true" data-testid={testId} data-sku={skuCode}>
      {background ?? <div className={styles.backdropBlur} aria-hidden="true" />}
      <div className={styles.sheet}>
        <div className={styles.handle} aria-hidden="true" />
        <div className={styles.providerStrip} aria-hidden="true">
          <span className={styles.providerDot}>T</span>
          <span>{providerLabel}</span>
        </div>

        <div className={styles.invoiceRow}>
          <div className={styles.invoiceGlyph} aria-hidden="true">
            <InvoiceThumb thumbUrl={thumbUrl} glyph={glyph} className={styles.invoiceThumb} />
          </div>
          <div className={styles.invoiceCopy}>
            <div className={styles.invoiceTitle}>{title}</div>
            {subtitle ? <div className={styles.invoiceSub}>{subtitle}</div> : null}
          </div>
          <div className={styles.invoicePrice}>{priceStars} ⭐</div>
        </div>

        <div className={styles.bullets} role="list">
          {bullets.map((line) => (
            <div key={line} className={styles.bulletRow} role="listitem">
              <span className={styles.bulletCheck} aria-hidden="true">✓</span>
              <span>{line}</span>
            </div>
          ))}
        </div>

        {balanceHtml ? (
          <div className={styles.balanceNote} dangerouslySetInnerHTML={{ __html: balanceHtml }} />
        ) : (
          <div className={styles.balanceNote}>{balanceFallback}</div>
        )}

        {children}

        <PrimaryButton
          className={styles.payBtn}
          onClick={onPrimary}
          disabled={primaryDisabled}
          data-testid={payTestId}
        >
          {ctaLabel}
        </PrimaryButton>
        <SecondaryButton className={styles.cancelBtn} onClick={onCancel} data-testid={cancelTestId}>
          {cancelLabel}
        </SecondaryButton>
      </div>
    </div>
  );
}

export default InvoiceSheetShell;
